import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Stethoscope, BarChart3, HelpCircle, LogIn, LogOut, User, Library, ArrowLeft, Sparkles, RefreshCw } from "lucide-react";
import { toast } from "sonner";

import GameRow from "@/components/GameRow";
import Combobox from "@/components/Combobox";
import ResultsModal from "@/components/ResultsModal";
import StatsModal from "@/components/StatsModal";
import HowToModal from "@/components/HowToModal";

import {
  fetchToday,
  fetchCaseById,
  fetchDisciplines,
  fetchVersion,
  submitGuess,
  submitDiagnosis,
  fetchMe,
  fetchStatsMe,
  syncStats,
  logout,
} from "@/lib/api";
import {
  loadState,
  saveState,
  loadStats,
  loadDiscipline,
  saveDiscipline,
  updateStatsAfterGame,
  caseFingerprint,
} from "@/lib/storage";

const MAX_GUESSES = 5;
const DEFAULT_DISCIPLINE = "General Medicine";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirect = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
};

export default function Home() {
  const params = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const archiveCaseId = params.caseId || null; // present when /play/:caseId
  const isArchive = !!archiveCaseId;

  // Discipline state (only meaningful for the live "/" route)
  const initialDiscipline =
    searchParams.get("discipline") || loadDiscipline() || DEFAULT_DISCIPLINE;
  const [discipline, setDiscipline] = useState(initialDiscipline);
  const [disciplines, setDisciplines] = useState([]);

  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [asking, setAsking] = useState(false);
  const [askedIds, setAskedIds] = useState(new Set());
  const [selectedDx, setSelectedDx] = useState("");
  const [result, setResult] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [stats, setStats] = useState(loadStats());
  const [user, setUser] = useState(null);

  const [showResults, setShowResults] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showHow, setShowHow] = useState(false);

  // Load disciplines once
  useEffect(() => {
    (async () => {
      try {
        const d = await fetchDisciplines();
        setDisciplines(d.items || []);
        // If the persisted discipline isn't in the canonical list any more
        // (e.g. earlier capitalization drift), reset to the server default.
        const items = d.items || [];
        if (items.length && !items.includes(discipline)) {
          // Case-insensitive match first, then fall back to server default.
          const match = items.find(
            (s) => s.toLowerCase() === (discipline || "").toLowerCase()
          );
          setDiscipline(match || d.default || items[0]);
        }
      } catch {
        setDisciplines([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset local game state whenever the target case (archive OR discipline+today) changes
  useEffect(() => {
    setCaseData(null);
    setLoading(true);
    setRows([]);
    setAskedIds(new Set());
    setSelectedDx("");
    setResult(null);
    setGameOver(false);
  }, [archiveCaseId, discipline]);

  // Persist discipline pick (only on live route)
  useEffect(() => {
    if (!isArchive) {
      saveDiscipline(discipline);
      const cur = searchParams.get("discipline");
      if (cur !== discipline) {
        const next = new URLSearchParams(searchParams);
        next.set("discipline", discipline);
        setSearchParams(next, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discipline, isArchive]);

  // Fetch case + user + hydrate any saved state
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const casePromise = isArchive
          ? fetchCaseById(archiveCaseId)
          : fetchToday(discipline);
        const [c, u] = await Promise.all([casePromise, fetchMe()]);
        if (cancelled) return;
        setCaseData(c);
        setUser(u);
        const fp = caseFingerprint(c);
        const saved = loadState(c.case_id, fp);
        if (saved) {
          setRows(saved.rows || []);
          setAskedIds(new Set(saved.askedIds || []));
          if (saved.result) {
            setResult(saved.result);
            setGameOver(true);
          }
        }
        if (u && !isArchive) {
          try {
            const s = await fetchStatsMe();
            setStats((prev) => ({
              played: s.played,
              wins: s.wins,
              current_streak: s.current_streak,
              max_streak: s.max_streak,
              last_date: prev.last_date,
              by_discipline: s.by_discipline || null,
            }));
          } catch {
            /* stats optional */
          }
        }
      } catch (e) {
        toast.error(isArchive ? "Could not load that case" : `Could not load today's ${discipline} case`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [archiveCaseId, discipline, isArchive]);

  // Persist state per case_id
  useEffect(() => {
    if (!caseData) return;
    saveState(caseData.case_id, {
      case_id: caseData.case_id,
      fingerprint: caseFingerprint(caseData),
      rows,
      askedIds: Array.from(askedIds),
      result,
    });
  }, [caseData, rows, askedIds, result]);

  // First-visit tutorial
  useEffect(() => {
    if (!loading && !localStorage.getItem("dxdaily:seenIntro")) {
      setShowHow(true);
      localStorage.setItem("dxdaily:seenIntro", "1");
    }
  }, [loading]);

  const questionItems = useMemo(() => {
    if (!caseData) return [];
    return caseData.questions.map((q) => ({
      value: String(q.id),
      label: q.text,
      disabled: askedIds.has(q.id),
    }));
  }, [caseData, askedIds]);

  const dxItems = useMemo(() => {
    if (!caseData) return [];
    return caseData.diagnosis_options.map((d) => ({ value: d, label: d }));
  }, [caseData]);

  const disciplineItems = useMemo(
    () => disciplines.map((d) => ({ value: d, label: d })),
    [disciplines]
  );

  const handleAsk = async (qid) => {
    // Nullish check — `qid` can legitimately be 0 (question ids are 0-indexed
    // by the LLM), and !0 is truthy so a plain `!qid` guard would silently
    // eat the very first item in the list.
    if (qid == null || asking) return;
    if (askedIds.has(qid)) return;
    setAsking(true);
    try {
      const resp = await submitGuess(caseData.case_id, qid);
      const q = caseData.questions.find((x) => x.id === qid);
      setRows((r) => [...r, { id: qid, text: q.text, relevant: resp.relevant }]);
      setAskedIds((s) => new Set(s).add(qid));
    } catch (e) {
      toast.error("Could not submit question");
    } finally {
      setAsking(false);
    }
  };

  const handleSubmitDx = async () => {
    if (!selectedDx) {
      toast.error("Pick a diagnosis first");
      return;
    }
    try {
      const res = await submitDiagnosis(caseData.case_id, selectedDx);
      // Persist the player's own pick alongside the server response so a
      // refresh still shows "You picked: X" until the next daily rollover.
      const resultWithPick = { ...res, player_diagnosis: selectedDx };
      setResult(resultWithPick);
      setGameOver(true);
      const greenCount = rows.filter((r) => r.relevant).length;
      if (!isArchive) {
        const nextStats = updateStatsAfterGame(res.correct, caseData.date);
        setStats(nextStats);
        if (user) {
          try {
            const s = await syncStats({
              date: caseData.date,
              won: res.correct,
              green_count: greenCount,
              guesses_used: rows.length,
              case_id: caseData.case_id,
              specialty: caseData.specialty,
            });
            setStats({
              played: s.played,
              wins: s.wins,
              current_streak: s.current_streak,
              max_streak: s.max_streak,
              last_date: caseData.date,
              by_discipline: s.by_discipline || null,
            });
          } catch {
            /* sync best-effort */
          }
        }
      }
      setShowResults(true);
    } catch (e) {
      toast.error("Could not submit diagnosis");
    }
  };

  const handleLogout = async () => {
    await logout().catch(() => {});
    setUser(null);
    toast.success("Signed out");
  };

  const handleForceRefresh = () => {
    try {
      // Preserve completed case state and streak stats so the player's
      // "locked until 00:00 PT" summary survives a force-refresh. Only wipe
      // the discipline selection and the how-to flag; leave state:v3:* and
      // stats intact.
      const preservePrefixes = ["dxdaily:state:v3:", "dxdaily:stats", "dxdaily:adminToken"];
      Object.keys(localStorage)
        .filter((k) => k.startsWith("dxdaily:"))
        .filter((k) => !preservePrefixes.some((p) => k.startsWith(p)))
        .forEach((k) => localStorage.removeItem(k));
    } catch {
      /* ignore */
    }
    // Cache-bust the JS/HTML bundle by forcing a full navigation with a query stamp.
    window.location.href = `/?_=${Date.now()}`;
  };

  // ---- Live version heartbeat ----
  // Detect when the server has rolled a new build so we can nudge the user to
  // refresh instead of them staring at a stale cached bundle for hours.
  const [bootVersion, setBootVersion] = useState(null);
  const [serverVersion, setServerVersion] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { version } = await fetchVersion();
        if (!cancelled) {
          setBootVersion(version);
          setServerVersion(version);
        }
      } catch { /* offline is fine */ }
    })();
    const id = setInterval(async () => {
      try {
        const { version } = await fetchVersion();
        if (!cancelled) setServerVersion(version);
      } catch { /* offline is fine */ }
    }, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  const updateAvailable = !!(bootVersion && serverVersion && bootVersion !== serverVersion);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 gap-3">
        <Sparkles className="h-5 w-5 text-slate-500 animate-pulse" />
        <div className="text-slate-500 text-sm font-mono tracking-[0.2em] uppercase">
          {isArchive ? "Loading case…" : `Loading ${discipline}…`}
        </div>
        {!isArchive && (
          <div className="text-slate-400 text-xs mt-1 max-w-xs text-center leading-relaxed">
            First visit for this discipline today may take up to 30 s while the AI drafts the case.
          </div>
        )}
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500">No case available.</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-2xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <Stethoscope className="h-5 w-5 text-slate-900 shrink-0" />
            <button
              data-testid="brand"
              onClick={() => navigate("/")}
              className="text-lg font-bold tracking-tight active:scale-95 transition-transform"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              DxDaily
            </button>
            <span className="ml-1 hidden sm:inline text-[10px] font-mono tracking-widest uppercase text-slate-400 border border-slate-200 rounded-full px-2 py-0.5 truncate">
              {caseData.specialty}
              {caseData.difficulty ? ` · ${caseData.difficulty}` : ""}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              data-testid="archive-btn"
              onClick={() => navigate("/archive")}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium text-slate-700 hover:bg-slate-100 active:scale-95 transition-all"
            >
              <Library className="h-4 w-4" /> Archive
            </button>
            <button
              data-testid="howto-btn"
              onClick={() => setShowHow(true)}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-700 active:scale-95 transition-all"
              aria-label="How to play"
            >
              <HelpCircle className="h-5 w-5" />
            </button>
            <button
              data-testid="stats-btn"
              onClick={() => setShowStats(true)}
              className="p-2 rounded-full hover:bg-slate-100 text-slate-700 active:scale-95 transition-all"
              aria-label="Stats"
            >
              <BarChart3 className="h-5 w-5" />
            </button>
            {user ? (
              <button
                data-testid="logout-btn"
                onClick={handleLogout}
                className="ml-1 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:border-slate-500 active:scale-95 transition-all"
              >
                <User className="h-3.5 w-3.5" />
                <span className="max-w-[80px] truncate">{user.name?.split(" ")[0] || "You"}</span>
                <LogOut className="h-3.5 w-3.5 opacity-60" />
              </button>
            ) : (
              <button
                data-testid="login-btn"
                onClick={startGoogleLogin}
                className="ml-1 inline-flex items-center gap-2 rounded-full bg-slate-900 text-white px-3 py-1.5 text-xs font-medium hover:-translate-y-0.5 active:scale-95 transition-all"
              >
                <LogIn className="h-3.5 w-3.5" /> Sign in
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 md:py-10 space-y-6">
        {updateAvailable && (
          <button
            data-testid="update-available-pill"
            onClick={handleForceRefresh}
            className="w-full flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 hover:bg-emerald-100 active:scale-[0.99] transition-all"
          >
            <div className="flex items-center gap-2 text-emerald-900 text-sm text-left">
              <RefreshCw className="h-4 w-4 shrink-0" />
              <span>
                A new version is available.{" "}
                <b>Click to update.</b>
              </span>
            </div>
            <span className="text-[10px] font-mono text-emerald-800 shrink-0">
              v.{bootVersion} → v.{serverVersion}
            </span>
          </button>
        )}
        {isArchive && (
          <div
            data-testid="archive-banner"
            className="flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3"
          >
            <div className="flex items-center gap-2 text-amber-900 text-sm">
              <Library className="h-4 w-4" />
              <span>Archive mode — this play does not affect your streak.</span>
            </div>
            <button
              data-testid="back-to-today-btn"
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 hover:underline"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Today
            </button>
          </div>
        )}

        {/* Discipline picker — only on live route */}
        {!isArchive && disciplineItems.length > 0 && (
          <div className="space-y-2" data-testid="discipline-picker">
            <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
              Your discipline
            </p>
            <Combobox
              items={disciplineItems}
              value={discipline}
              onSelect={(v) => setDiscipline(v)}
              placeholder="Pick your discipline…"
              triggerTestId="discipline-dropdown-trigger"
              itemTestIdPrefix="discipline-item"
            />
          </div>
        )}

        {/* Date row */}
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0">
            <div
              data-testid="today-badge"
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-mono tracking-[0.2em] uppercase ${
                isArchive
                  ? "bg-amber-100 text-amber-800 border border-amber-200"
                  : "bg-emerald-100 text-emerald-800 border border-emerald-200"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${isArchive ? "bg-amber-500" : "bg-emerald-500 animate-pulse"}`} />
              {isArchive ? "Archive" : "Today"} · {caseData.date} · {caseData.specialty}
            </div>
            <h1
              className="text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 mt-2"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Diagnose the condition.
            </h1>
          </div>
          <div
            data-testid="streak-badge"
            className="hidden sm:flex flex-col items-end shrink-0"
          >
            <span className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
              Streak
            </span>
            <span className="text-2xl font-bold font-mono text-slate-900">
              {stats.current_streak}
            </span>
          </div>
        </div>

        {/* Case vignette */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          data-testid="case-card"
          className="bg-white border border-slate-200 rounded-2xl p-6 md:p-8 shadow-[0_2px_8px_rgba(15,23,42,0.04)]"
        >
          <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500 mb-3">
            Clinical condition
          </p>
          <p className="text-base sm:text-lg leading-relaxed text-slate-800">
            {caseData.vignette}
          </p>
        </motion.div>

        {/* Board — each active slot owns its own inline question picker */}
        <div className="space-y-3">
          <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
            Your questions
            <span className="ml-2 text-slate-400">
              ({rows.length} / {MAX_GUESSES})
            </span>
            {!gameOver && rows.length < MAX_GUESSES && (
              <span className="ml-2 text-slate-400">
                — pick a question in slot Q{rows.length + 1}
              </span>
            )}
          </p>
          <div className="flex flex-col gap-3">
            {Array.from({ length: MAX_GUESSES }).map((_, i) => {
              const isActive = !gameOver && i === rows.length;
              return (
                <GameRow
                  key={i}
                  index={i}
                  row={rows[i]}
                  active={isActive}
                  items={questionItems}
                  onPick={handleAsk}
                  loading={isActive && asking}
                />
              );
            })}
          </div>
        </div>

        {/* Diagnosis */}
        {!gameOver && (
          <div className="space-y-2 pt-2 border-t border-slate-200">
            <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-slate-500">
              Final diagnosis
            </p>
            <p className="text-xs text-slate-500">
              You can commit at any time — you don&apos;t have to use all 5 questions.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <Combobox
                  items={dxItems}
                  value={selectedDx}
                  onSelect={setSelectedDx}
                  placeholder="Choose the single most likely diagnosis…"
                  triggerTestId="diagnosis-dropdown-trigger"
                  itemTestIdPrefix="diagnosis-item"
                />
              </div>
              <button
                data-testid="submit-diagnosis-btn"
                onClick={handleSubmitDx}
                disabled={!selectedDx}
                className="rounded-full bg-emerald-600 text-white px-6 py-3 text-sm font-medium hover:bg-emerald-700 hover:-translate-y-0.5 active:scale-95 transition-all disabled:cursor-not-allowed disabled:opacity-40 disabled:transform-none"
              >
                Submit
              </button>
            </div>
          </div>
        )}

        {gameOver && result && (
          <div className="pt-2 border-t border-slate-200 space-y-3" data-testid="post-game-summary">
            <div
              className={`rounded-2xl border p-4 sm:p-5 ${
                result.correct
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-red-200 bg-red-50"
              }`}
            >
              <div className="flex items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest ${
                    result.correct
                      ? "bg-emerald-500 text-white"
                      : "bg-red-500 text-white"
                  }`}
                  data-testid="post-game-badge"
                >
                  {result.correct ? "✓ Correct" : "✗ Incorrect"}
                </span>
                <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">
                  Case closed · locked until 00:00 PT
                </span>
              </div>
              {result.player_diagnosis && (
                <p className="mt-3 text-sm text-slate-700">
                  Your diagnosis:{" "}
                  <span
                    className="font-semibold text-slate-900"
                    data-testid="player-diagnosis"
                  >
                    {result.player_diagnosis}
                  </span>
                </p>
              )}
              {!result.correct && result.correct_diagnosis && (
                <p className="mt-1 text-sm text-slate-700">
                  Correct answer:{" "}
                  <span className="font-semibold text-emerald-700">
                    {result.correct_diagnosis}
                  </span>
                </p>
              )}
            </div>

            <div className="flex items-center justify-between">
              <p className="text-xs text-slate-500">
                Come back after midnight Pacific for the next case.
              </p>
              <button
                data-testid="view-results-btn"
                onClick={() => setShowResults(true)}
                className="rounded-full bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:-translate-y-0.5 active:scale-95 transition-all"
              >
                View full results
              </button>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-400 pt-6 pb-2">
          A new case for every discipline, every day at 00:00 Pacific Time.
        </p>
      </main>

      <ResultsModal
        open={showResults}
        onOpenChange={setShowResults}
        result={result}
        rows={rows}
        date={caseData.date}
        specialty={caseData.specialty}
        stats={stats}
      />
      <StatsModal open={showStats} onOpenChange={setShowStats} stats={stats} user={user} />
      <HowToModal open={showHow} onOpenChange={setShowHow} />
    </div>
  );
}
